import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KPICard } from "@/components/collections/kpi-card";
import { LogCallModal, SendEmailModal, RecordPromiseModal } from "@/components/collections/action-modals";
import { CaseDetailsDialog } from "@/components/collections/case-details-dialog";
import { CreateCaseDrawer } from "@/components/collections/create-case-drawer";
import { ColumnConfigDialog } from "@/components/collections/column-config-dialog";
import { useColumnPreferences } from "@/hooks/useColumnPreferences";
import {
  Search,
  Phone,
  Mail,
  FileText,
  Clock,
  Inbox,
  AlertTriangle,
  PoundSterling,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
  User,
  FileSpreadsheet,
  Plus,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";
import { CollectionCase } from "@/types/collections";
import { exportCollectionCasesToExcel } from "@/lib/collections-export-utils";

export default function CollectionsQueue() {
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get("filter");
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active"); // Default to Active cases only
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedCase, setSelectedCase] = useState<CollectionCase | null>(null);
  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<string>>(new Set());
  
  // Modal states
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [recordPromiseOpen, setRecordPromiseOpen] = useState(false);
  const [caseDetailsOpen, setCaseDetailsOpen] = useState(false);
  const [createCaseOpen, setCreateCaseOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Column preferences
  const { columns, visibleColumns, savePreferences, resetToDefaults } = useColumnPreferences();

  // Sorting state - Default sort: Action Due Date (asc), then Priority (desc)
  type SortField = "priority" | "caseId" | "customerName" | "amount" | "daysInCollection" | "nextAction" | "nextActionDate" | "status" | "strategy";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>("nextActionDate"); // Default primary sort
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc"); // Ascending - oldest dates first

  // Use global data store to ensure consistency with Homepage
  const [cases] = useState(() => getGlobalDataStore().getAllCases());

  // Handle URL filter parameter
  useEffect(() => {
    if (filterParam === "action-due-today") {
      // When coming from Homepage "Cases Due" metric, show all statuses
      setStatusFilter("all");
    }
  }, [filterParam]);

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> back to default
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        // Reset to default sort (Action Due Date ascending)
        setSortField("nextActionDate");
        setSortDirection("asc");
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // Filter and sort cases
  const filteredAndSortedCases = useMemo(() => {
    // First, filter the cases
    let result = cases.filter((caseItem) => {
      const matchesSearch =
        caseItem.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        caseItem.caseId.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || caseItem.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || caseItem.priority === priorityFilter;

      // Handle "action-due-today" filter from Homepage
      let matchesActionDueToday = true;
      if (filterParam === "action-due-today") {
        const daysDiff = differenceInDays(new Date(caseItem.nextActionDate), new Date());
        matchesActionDueToday = daysDiff === 0;
      }

      return matchesSearch && matchesStatus && matchesPriority && matchesActionDueToday;
    });

    // Then, apply sorting if a sort field is selected
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        // Custom priority order: High > Medium > Low
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };

        switch (sortField) {
          case "priority":
            aValue = priorityOrder[a.priority as keyof typeof priorityOrder];
            bValue = priorityOrder[b.priority as keyof typeof priorityOrder];
            break;
          case "caseId":
            aValue = a.caseId.toLowerCase();
            bValue = b.caseId.toLowerCase();
            break;
          case "customerName":
            aValue = a.customerName.toLowerCase();
            bValue = b.customerName.toLowerCase();
            break;
          case "amount":
            aValue = a.amount;
            bValue = b.amount;
            break;
          case "daysInCollection":
            aValue = a.daysInCollection;
            bValue = b.daysInCollection;
            break;
          case "nextAction":
            aValue = a.nextAction.toLowerCase();
            bValue = b.nextAction.toLowerCase();
            break;
          case "nextActionDate":
            aValue = new Date(a.nextActionDate).getTime();
            bValue = new Date(b.nextActionDate).getTime();
            break;
          case "status":
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          case "strategy":
            aValue = a.strategy.name.toLowerCase();
            bValue = b.strategy.name.toLowerCase();
            break;
          default:
            return 0;
        }

        // Primary sort comparison
        let comparison = 0;
        if (aValue < bValue) comparison = sortDirection === "asc" ? -1 : 1;
        else if (aValue > bValue) comparison = sortDirection === "asc" ? 1 : -1;

        // If primary sort values are equal and sorting by nextActionDate, apply secondary sort by Priority (desc)
        if (comparison === 0 && sortField === "nextActionDate") {
          const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder];
          const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder];
          // Descending order for priority (High priority first)
          if (aPriority > bPriority) return -1;
          if (aPriority < bPriority) return 1;
        }

        return comparison;
      });
    }

    return result;
  }, [cases, searchQuery, statusFilter, priorityFilter, sortField, sortDirection, filterParam]);

  // Keep filteredCases for backward compatibility with KPI calculations
  const filteredCases = filteredAndSortedCases;

  // Calculate KPIs
  const kpis = useMemo(() => {
    const highPriority = filteredCases.filter((c) => c.priority === "High").length;
    const dueToday = filteredCases.filter((c) => {
      const daysDiff = differenceInDays(new Date(c.nextActionDate), new Date());
      return daysDiff === 0;
    }).length;
    const totalOutstanding = filteredCases.reduce((sum, c) => sum + c.amount, 0);
    const avgDays = Math.round(
      filteredCases.reduce((sum, c) => sum + c.daysInCollection, 0) / filteredCases.length
    );

    return {
      myCases: { total: cases.length, filtered: filteredCases.length },
      highPriority,
      dueToday,
      totalOutstanding,
      avgDays,
    };
  }, [cases, filteredCases]);

  const handleViewCaseDetails = (caseItem: CollectionCase) => {
    setSelectedCase(caseItem);
    setCaseDetailsOpen(true);
  };

  const handleAction = (action: string, caseItem: CollectionCase) => {
    setSelectedCase(caseItem);
    switch (action) {
      case "call":
        setLogCallOpen(true);
        break;
      case "email":
        setSendEmailOpen(true);
        break;
      case "promise":
        setRecordPromiseOpen(true);
        break;
      default:
        break;
    }
  };

  // Checkbox selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredCases.map((c) => c.id));
      setSelectedCaseIds(allIds);
    } else {
      setSelectedCaseIds(new Set());
    }
  };

  const handleSelectCase = (caseId: string, checked: boolean) => {
    const newSelected = new Set(selectedCaseIds);
    if (checked) {
      newSelected.add(caseId);
    } else {
      newSelected.delete(caseId);
    }
    setSelectedCaseIds(newSelected);
  };

  // Bulk action handlers
  const handleBulkAction = (action: string) => {
    if (selectedCaseIds.size === 0) return;
    
    // For single selection, use the first selected case
    const firstSelectedId = Array.from(selectedCaseIds)[0];
    const firstCase = cases.find((c) => c.id === firstSelectedId);
    
    if (firstCase) {
      setSelectedCase(firstCase);
      switch (action) {
        case "call":
          setLogCallOpen(true);
          break;
        case "email":
          setSendEmailOpen(true);
          break;
        case "promise":
          setRecordPromiseOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const selectedCases = useMemo(() => {
    return cases.filter((c) => selectedCaseIds.has(c.id));
  }, [cases, selectedCaseIds]);

  const isAllSelected = filteredCases.length > 0 && selectedCaseIds.size === filteredCases.length;
  const isSomeSelected = selectedCaseIds.size > 0 && selectedCaseIds.size < filteredCases.length;

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  const isOverdue = (date: Date) => {
    return differenceInDays(new Date(), new Date(date)) > 0;
  };

  const isDueToday = (date: Date) => {
    return differenceInDays(new Date(date), new Date()) === 0;
  };

  // Export handler
  const handleExportToExcel = async () => {
    if (filteredCases.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      exportCollectionCasesToExcel(filteredCases);
    } catch (error) {
      console.error("Error exporting cases:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Collections Queue</h1>
          <p className="text-muted-foreground">Manage your assigned collection cases</p>
        </div>
        <Button onClick={() => setCreateCaseOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create New Case
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="My Cases"
          value={kpis.myCases.filtered}
          subtitle={`${kpis.myCases.total} total cases`}
          icon={Inbox}
          colorScheme="blue"
        />
        <KPICard
          title="High Priority"
          value={kpis.highPriority}
          subtitle="Requiring immediate attention"
          icon={AlertTriangle}
          colorScheme="red"
        />
        <KPICard
          title="Due Today"
          value={kpis.dueToday}
          subtitle="Actions scheduled for today"
          icon={Calendar}
          colorScheme="amber"
        />
        <KPICard
          title="Total Outstanding"
          value={`£${kpis.totalOutstanding.toLocaleString()}`}
          subtitle={`Avg ${kpis.avgDays} days in collection`}
          icon={PoundSterling}
          colorScheme="purple"
        />
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>My Cases</CardTitle>
              <CardDescription>View and manage your assigned collection cases</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleExportToExcel}
                      disabled={isExporting || filteredCases.length === 0}
                      aria-label="Export to Excel"
                    >
                      <FileSpreadsheet className={cn("h-4 w-4", isExporting && "animate-pulse")} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Export to Excel</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      aria-label="Customize Columns"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Customize Columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer name or case ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Cases Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all cases"
                      className={cn(isSomeSelected && !isAllSelected && "data-[state=checked]:bg-primary")}
                    />
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("priority")}
                    >
                      <span>Priority</span>
                      {getSortIcon("priority")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("caseId")}
                    >
                      <span>Case ID</span>
                      {getSortIcon("caseId")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("customerName")}
                    >
                      <span>Customer</span>
                      {getSortIcon("customerName")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-mr-3 h-8 data-[state=open]:bg-accent ml-auto flex"
                      onClick={() => handleSort("amount")}
                    >
                      <span>Amount</span>
                      {getSortIcon("amount")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("daysInCollection")}
                    >
                      <span>Days</span>
                      {getSortIcon("daysInCollection")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("nextAction")}
                    >
                      <span>Next Action</span>
                      {getSortIcon("nextAction")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("nextActionDate")}
                    >
                      <span>Action Due Date</span>
                      {getSortIcon("nextActionDate")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("status")}
                    >
                      <span>Status</span>
                      {getSortIcon("status")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("strategy")}
                    >
                      <span>Collection Strategy</span>
                      {getSortIcon("strategy")}
                    </Button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-muted-foreground">
                      No cases found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCases.map((caseItem) => (
                    <TableRow 
                      key={caseItem.id}
                      className={cn(
                        selectedCaseIds.has(caseItem.id) && "bg-muted/50"
                      )}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedCaseIds.has(caseItem.id)}
                          onCheckedChange={(checked) => handleSelectCase(caseItem.id, checked as boolean)}
                          aria-label={`Select case ${caseItem.caseId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPriorityBadgeVariant(caseItem.priority)}>
                          {caseItem.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        <Button 
                          variant="link" 
                          className="h-auto p-0 font-medium hover:underline"
                          onClick={() => handleViewCaseDetails(caseItem)}
                        >
                          {caseItem.caseId}
                        </Button>
                      </TableCell>
                      <TableCell>{caseItem.customerName}</TableCell>
                      <TableCell className="text-right font-medium">
                        £{caseItem.amount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">{caseItem.daysInCollection}d</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{caseItem.nextAction}</p>
                      </TableCell>
                      <TableCell>
                        <p
                          className={cn(
                            "text-sm",
                            isOverdue(caseItem.nextActionDate)
                              ? "text-red-600 dark:text-red-400 font-medium"
                              : isDueToday(caseItem.nextActionDate)
                              ? "text-amber-600 dark:text-amber-400 font-medium"
                              : "text-muted-foreground"
                          )}
                        >
                          {format(new Date(caseItem.nextActionDate), "MMM d, yyyy")}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(caseItem.status)}>
                          {caseItem.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm truncate max-w-[200px] block">
                                {caseItem.strategy.name}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{caseItem.strategy.name}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Results Summary */}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredCases.length} of {cases.length} cases
          </div>
        </CardContent>
      </Card>

      {/* Floating Quick Actions Menu */}
      {selectedCaseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5">
          <Card className="shadow-lg border-2">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-sm font-semibold">
                    {selectedCaseIds.size} {selectedCaseIds.size === 1 ? "case" : "cases"} selected
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedCaseIds(new Set())}
                    className="h-8 px-2"
                  >
                    Clear
                  </Button>
                </div>
                <div className="h-6 w-px bg-border" />
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction("call")}
                    className="gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    Log Call
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction("email")}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Send Email
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkAction("promise")}
                    className="gap-2"
                  >
                    <FileText className="h-4 w-4" />
                    Record Promise
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Action Modals */}
      {selectedCase && (
        <>
          <LogCallModal
            open={logCallOpen}
            onOpenChange={setLogCallOpen}
            customerName={selectedCase.customerName}
            customerPhone="(555) 123-4567"
          />
          <SendEmailModal
            open={sendEmailOpen}
            onOpenChange={setSendEmailOpen}
            customerName={selectedCase.customerName}
            customerEmail="customer@example.com"
          />
          <RecordPromiseModal
            open={recordPromiseOpen}
            onOpenChange={setRecordPromiseOpen}
            customerName={selectedCase.customerName}
          />
        </>
      )}

      {/* Case Details Dialog */}
      <CaseDetailsDialog
        open={caseDetailsOpen}
        onOpenChange={setCaseDetailsOpen}
        caseData={selectedCase}
      />

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Create Case Drawer */}
      <CreateCaseDrawer
        open={createCaseOpen}
        onOpenChange={setCreateCaseOpen}
        onCaseCreated={() => {
          // In a real implementation, this would refresh the cases list
          // For now, we just show a success message (handled in the drawer)
          console.log("Case created successfully");
        }}
      />
    </div>
  );
}
